import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrompts } from '../context/PromptContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PreviewPane from './PreviewPane';
import CollapsibleSection from './CollapsibleSection';
import MobileBottomSheet from './MobileBottomSheet';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPlus, FiTrash2, FiMove, FiSave, FiSearch, FiLayers, FiEye, FiArrowUp, FiArrowDown, FiDatabase } = FiIcons;

const CompositionMode = () => {
  const navigate = useNavigate();
  const { prompts, addPrompt } = usePrompts();
  const { success: showSuccess, error: showError } = useToast();
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [separator, setSeparator] = useState('\n\n---\n\n');
  const [composedTitle, setComposedTitle] = useState('');
  const [isLibrarySheetOpen, setIsLibrarySheetOpen] = useState(false);

  const {
    state: saveState,
    error: saveError,
    handleAction: handleSaveAction,
    reset: resetSaveState,
    icon: saveIcon,
    text: saveText,
    classes: saveClasses,
  } = useButtonState({
    idleText: 'Save Merged Template',
    loadingText: 'Saving...',
    successText: 'Saved!',
    failedText: 'Failed',
    idleIcon: FiSave,
    onSuccess: () => {
      showSuccess('Composition saved successfully!');
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || 'Failed to save composition';
      showError(errorMsg);
    },
    autoResetDelay: 2000,
  });

  const filteredPrompts = prompts.filter(prompt =>
    prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prompt.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToComposition = (prompt) => {
    // Allow duplicates in composition, but give unique ID
    setSelectedPrompts([...selectedPrompts, { ...prompt, compositionId: Date.now() + Math.random() }]);
  };

  const removeFromComposition = (compositionId) => {
    setSelectedPrompts(selectedPrompts.filter(p => p.compositionId !== compositionId));
  };

  const moveItem = (index, direction) => {
    const newSelected = [...selectedPrompts];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newSelected.length) return;

    [newSelected[index], newSelected[newIndex]] = [newSelected[newIndex], newSelected[index]];
    setSelectedPrompts(newSelected);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(selectedPrompts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSelectedPrompts(items);
  };

  const generateComposedPrompt = () => {
    return selectedPrompts
      .map(prompt => prompt.content)
      .join(separator);
  };

  const composedPrompt = generateComposedPrompt();

  const handleSaveComposition = async () => {
    const validationError = !composedTitle.trim() ? 'Title is required' : selectedPrompts.length === 0 ? 'Select at least one prompt' : null;

    if (validationError) {
      showError(validationError);
      resetSaveState();
      return;
    }

    const composedContent = generateComposedPrompt();
    const allTags = [...new Set(selectedPrompts.flatMap(p => p.tags || []))];

    const newPrompt = {
      title: composedTitle.trim(),
      content: composedContent,
      tags: [...allTags, 'composed'],
    };

    await handleSaveAction(async () => {
      await addPrompt(newPrompt);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    });
  };

  // Extract placeholders from the final composed string
  const placeholders = useMemo(() => {
    const matches = composedPrompt.match(/\{[^}]+\}/g) || [];
    return [...new Set(matches)];
  }, [composedPrompt]);

  return (
    <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-0 pb-bottom-nav h-[calc(100vh-172px)] md:h-[calc(100vh-100px)] flex flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4 flex-shrink-0">
      <div className="flex items-center space-x-4 w-full md:w-auto">
        <div className="flex-1 md:flex-none flex items-center gap-4">
           <div className="p-2 border-2 border-black bg-white shadow-neo min-w-[44px] min-h-[44px] flex items-center justify-center">
             <SafeIcon icon={FiLayers} size={32} />
           </div>
           <h1 className="text-2xl md:text-3xl font-black text-black uppercase">
            Composition
          </h1>
        </div>
      </div>
        <div className="relative">
          <button
            onClick={handleSaveComposition}
            disabled={!composedTitle.trim() || selectedPrompts.length === 0 || saveState === 'loading'}
            className={`neo-btn neo-btn-primary w-full md:w-auto flex items-center justify-center transition-all ${saveClasses} ${
              composedTitle.trim() && selectedPrompts.length > 0 && saveState !== 'loading'
                ? 'hover:cursor-pointer hover:scale-105'
                : 'cursor-not-allowed'
            }`}
          >
            {saveIcon && <SafeIcon icon={saveIcon} className="mr-2" size={16} />}
            <span>{saveText}</span>
          </button>
          {saveState === 'failed' && saveError && (
            <div className="absolute top-full right-0 mt-2 bg-red-100 border-2 border-red-600 p-2 text-xs text-red-800 font-bold max-w-xs z-50">
              {saveError}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
        {/* Left Column: Library */}
        <div className="hidden md:flex md:flex-col lg:col-span-1 h-full min-h-0 overflow-hidden">
          <div className="neo-card p-0 flex flex-col h-full overflow-hidden">
            <div className="neo-window-header bg-neo-blue flex-shrink-0">
              <span>Library</span>
            </div>
            <div className="p-4 border-b-2 border-black bg-white flex-shrink-0">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black" size={16} />
                <input
                  type="text"
                  placeholder="Search Library..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="neo-input pl-9 h-12 text-sm input-touch"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {filteredPrompts.length === 0 ? (
                 <div className="text-center text-gray-400 font-mono text-sm mt-4">No templates found.</div>
              ) : (
                filteredPrompts.map(prompt => (
                    <div
                    key={prompt.id}
                    className="p-3 border-2 border-black bg-white hover:bg-neo-yellow transition-all shadow-neo-sm cursor-pointer group"
                    onClick={() => addToComposition(prompt)}
                    >
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-black truncate uppercase">
                            {prompt.title}
                        </h3>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2 font-mono">
                            {prompt.content.substring(0, 80)}...
                        </p>
                         </div>
                         <div className="ml-2 bg-black text-white p-2 min-w-[44px] min-h-[44px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                         <SafeIcon icon={FiPlus} size={14} />
                         </div>
                     </div>
                    </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Center & Right: Composition & Preview */}
        <div className="lg:col-span-2 flex flex-col gap-6 h-full overflow-y-auto overscroll-contain touch-pan-y">

          <button
            className="md:hidden neo-btn w-full mb-4 flex items-center justify-center min-h-[44px]"
            onClick={() => setIsLibrarySheetOpen(true)}
          >
            <SafeIcon icon={FiDatabase} className="mr-2" size={18} />
            Open Library
          </button>

          <CollapsibleSection title="WORKBENCH" headerColor="bg-neo-red" defaultExpanded={true}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-black mb-1 uppercase">
                    New Title
                  </label>
                  <input
                    type="text"
                    value={composedTitle}
                    onChange={(e) => setComposedTitle(e.target.value)}
                    placeholder="My Composed Template"
                    className="neo-input input-touch"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-black mb-1 uppercase">
                    Separator
                  </label>
                  <input
                    type="text"
                    value={separator}
                    onChange={(e) => setSeparator(e.target.value)}
                    placeholder="Separator (e.g. ---)"
                    className="neo-input font-mono input-touch"
                  />
                </div>
              </div>

              <div className="border-2 border-black bg-gray-100 min-h-[200px] p-4">
                {selectedPrompts.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 py-10">
                    <SafeIcon icon={FiLayers} className="mb-2" size={32} />
                    <p className="font-mono text-sm uppercase text-center max-w-xs">
                      Select templates from Library to start composing
                    </p>
                  </div>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="composition">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-3"
                        >
                          {selectedPrompts.map((prompt, index) => (
                            <Draggable
                              key={prompt.compositionId}
                              draggableId={prompt.compositionId.toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                 <div
                                   ref={provided.innerRef}
                                   {...provided.draggableProps}
                                   className={`p-3 border-2 border-black bg-white transition-all ${
                                     snapshot.isDragging
                                       ? 'shadow-neo-lg rotate-1 z-50'
                                       : 'shadow-neo-sm'
                                   }`}
                                 >
                                  <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2 mb-2">
                                          <div
                                            {...provided.dragHandleProps}
                                            className="hidden md:block p-2 min-w-[44px] min-h-[44px] cursor-move bg-gray-200 border border-black hover:bg-gray-300 flex items-center justify-center"
                                          >
                                            <SafeIcon icon={FiMove} size={14} />
                                          </div>
                                          <div className="flex md:hidden items-center space-x-1">
                                            <button
                                              onClick={(e) => { e.stopPropagation(); moveItem(index, 'up'); }}
                                              disabled={index === 0}
                                              className="p-2 min-w-[44px] min-h-[44px] border-2 border-black bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                              aria-label="Move up"
                                            >
                                              <SafeIcon icon={FiArrowUp} size={14} />
                                            </button>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); moveItem(index, 'down'); }}
                                              disabled={index === selectedPrompts.length - 1}
                                              className="p-2 min-w-[44px] min-h-[44px] border-2 border-black bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                              aria-label="Move down"
                                            >
                                              <SafeIcon icon={FiArrowDown} size={14} />
                                            </button>
                                          </div>
                                         <span className="text-xs font-black bg-black text-white px-2 py-0.5">
                                           #{index + 1}
                                         </span>
                                         <h3 className="text-sm font-bold text-black uppercase">
                                           {prompt.title}
                                         </h3>
                                        </div>
                                        <p className="text-xs text-gray-600 font-mono pl-8 line-clamp-2">
                                          {prompt.content}
                                        </p>
                                      </div>
                                      <button
                                        onClick={() => removeFromComposition(prompt.compositionId)}
                                        className="ml-2 p-2 min-w-[44px] min-h-[44px] text-black hover:bg-red-200 border border-transparent hover:border-black transition-all flex items-center justify-center"
                                      >
                                        <SafeIcon icon={FiTrash2} size={16} />
                                      </button>
                                   </div>
                                 </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
            </div>
          </CollapsibleSection>

          {/* Interactive Preview */}
          {composedPrompt && (
            <CollapsibleSection title="PREVIEW" headerColor="bg-neo-green" defaultExpanded={false}>
              <PreviewPane
                content={composedPrompt}
                placeholders={placeholders}
              />
            </CollapsibleSection>
          )}
        </div>
      </div>

      <MobileBottomSheet isOpen={isLibrarySheetOpen} onClose={() => setIsLibrarySheetOpen(false)}>
        <div className="space-y-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black" size={16} />
            <input
              type="text"
              placeholder="Search Library..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="neo-input pl-9 h-12 text-sm input-touch"
            />
          </div>
          <div className="space-y-3">
            {filteredPrompts.length === 0 ? (
              <div className="text-center text-gray-400 font-mono text-sm mt-4">No templates found.</div>
            ) : (
              filteredPrompts.map(prompt => (
                <div
                  key={prompt.id}
                  className="p-3 border-2 border-black bg-white hover:bg-neo-yellow transition-all shadow-neo-sm cursor-pointer group"
                  onClick={() => {
                    addToComposition(prompt);
                    setIsLibrarySheetOpen(false);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-black truncate uppercase">
                        {prompt.title}
                      </h3>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2 font-mono">
                        {prompt.content.substring(0, 80)}...
                      </p>
                    </div>
                    <div className="ml-2 bg-black text-white p-2 min-w-[44px] min-h-[44px] flex items-center justify-center">
                      <SafeIcon icon={FiPlus} size={14} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </MobileBottomSheet>
    </div>
  );
};

export default CompositionMode;